"use client"

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid"
import Image from "next/image"
import Link from "next/link"
import { FC, MouseEventHandler, useCallback, useState } from "react"
import { usePopper } from "react-popper"
import { CloudIcon } from "domains/wordcloudplus/domains/cloudSync/components/CloudIcon"
import { useDb } from "domains/wordcloudplus/domains/database/database"
import { type Project } from "domains/wordcloudplus/domains/database/db"
import { useThumbnail } from "domains/wordcloudplus/domains/thumbnails/hooks/useThumbnail"
import { wordCloudsPath } from "domains/wordcloudplus/config"

interface Props {
    project: Project
    reload: () => void
    exportProjects: (ids: number[]) => void
}

export const ProjectCard: FC<Props> = ({ exportProjects, project, reload }) => {
    const { db } = useDb()
    const { thumbnail } = useThumbnail({ projectId: project.id })
    const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>()
    const [panelElement, setPanelElement] = useState<HTMLElement | null>()

    const { styles, attributes } = usePopper(buttonElement, panelElement, {
        placement: "bottom-end",
    })

    const onClickDelete: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault()
            if (db == null || project.id == null) return
            db.projects.delete(project.id).then(() => {
                reload()
            })
        },
        [db, project.id, reload]
    )

    const onClickExport: MouseEventHandler<HTMLButtonElement> = useCallback(
        (event) => {
            event.preventDefault()
            if (db == null || project.id == null) return
            exportProjects([project.id])
        },
        [db, exportProjects, project.id]
    )

    return (
        <div key={project.id} className="group relative rounded-lg hover:bg-slate-50">
            <Link href={`${wordCloudsPath}/${project.uuid}`}>
                <div className="relative block aspect-video w-full overflow-hidden rounded-lg">
                    {thumbnail ? (
                        <Image
                            src={thumbnail}
                            alt={project.name}
                            className="object-cover group-hover:opacity-75"
                            fill
                            sizes="100vw"
                        />
                    ) : (
                        <div className="flex size-full items-center justify-center bg-gray-100">
                            <span>Open me to generate a thumbnail</span>
                        </div>
                    )}
                </div>
                <div className="flex p-4">
                    <div className="flex-auto">
                        <h3 className="mt-2 text-lg text-gray-700 group-hover:underline">{project.name}</h3>
                        <div className="mt-1 flex items-center gap-x-2.5">
                            <p className="text-sm text-gray-500">
                                {new Date(project.createdAt).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </p>
                            <svg viewBox="0 0 2 2" className="size-1 flex-none fill-gray-400">
                                <circle cx={1} cy={1} r={1} />
                            </svg>
                            <CloudIcon enabled={project.config.cloudSync || false} />
                        </div>
                    </div>
                    <div className="flex-none">
                        <Popover className="relative">
                            <PopoverButton ref={setButtonElement} className="-mr-2 rounded-full p-2 hover:bg-slate-200">
                                <EllipsisVerticalIcon className="size-5" />
                            </PopoverButton>
                            <PopoverPanel ref={setPanelElement} style={styles.popper} {...attributes.popper}>
                                <div className="w-40 shrink rounded-xl bg-white p-2 text-sm leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                                    <button
                                        type="button"
                                        onClick={onClickExport}
                                        className="hover:text-primary-600 block w-full rounded-sm p-2 text-left hover:bg-slate-100"
                                    >
                                        Export Data
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClickDelete}
                                        className="hover:text-primary-600 block w-full rounded-sm p-2 text-left hover:bg-slate-100"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </PopoverPanel>
                        </Popover>
                    </div>
                </div>
            </Link>
        </div>
    )
}
